"use client";

import * as React from "react";
import { useStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Switch } from "@/components/ui/switch";
import { useTheme } from "next-themes";
import { Sun, Moon, RotateCcw, Type, AlignJustify, Pilcrow } from "lucide-react";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
};

export function SettingsMenu({ open, onOpenChange }: Props) {
  const settings = useStore((s) => s.settings);
  const setSettings = useStore((s) => s.setSettings);
  const resetSettings = useStore((s) => s.resetSettings);
  const { theme, setTheme } = useTheme();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Configurações de leitura</DialogTitle>
          <DialogDescription>
            Ajuste a tipografia e o tema. As preferências ficam salvas no seu navegador.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2 max-h-[60vh] overflow-y-auto">
          {/* Theme */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Tema</Label>
            <ToggleGroup
              type="single"
              value={theme ?? "light"}
              onValueChange={(v) => v && setTheme(v)}
              className="grid grid-cols-2 gap-2"
            >
              <ToggleGroupItem
                value="light"
                className="flex items-center gap-2 justify-center data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                <Sun className="h-3.5 w-3.5" /> Claro
              </ToggleGroupItem>
              <ToggleGroupItem
                value="dark"
                className="flex items-center gap-2 justify-center data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                <Moon className="h-3.5 w-3.5" /> Escuro
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          {/* Font family */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Família de fonte
            </Label>
            <ToggleGroup
              type="single"
              value={settings.fontFamily}
              onValueChange={(v) =>
                v && setSettings({ fontFamily: v as "serif" | "sans" | "mono" })
              }
              className="grid grid-cols-3 gap-2"
            >
              <ToggleGroupItem
                value="serif"
                className="flex flex-col items-center gap-1 py-2 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                <Type className="h-3.5 w-3.5" />
                <span className="text-[10px]" style={{ fontFamily: "var(--font-reader)" }}>
                  Serif
                </span>
              </ToggleGroupItem>
              <ToggleGroupItem
                value="sans"
                className="flex flex-col items-center gap-1 py-2 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                <Type className="h-3.5 w-3.5" />
                <span className="text-[10px]" style={{ fontFamily: "var(--font-geist-sans)" }}>
                  Sans
                </span>
              </ToggleGroupItem>
              <ToggleGroupItem
                value="mono"
                className="flex flex-col items-center gap-1 py-2 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                <Pilcrow className="h-3.5 w-3.5" />
                <span className="text-[10px]" style={{ fontFamily: "var(--font-geist-mono)" }}>
                  Mono
                </span>
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          {/* Font size */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Tamanho da fonte
              </Label>
              <span className="text-sm tabular-nums">{settings.fontSize}px</span>
            </div>
            <Slider
              min={14}
              max={40}
              step={1}
              value={[settings.fontSize]}
              onValueChange={(v) => setSettings({ fontSize: v[0] })}
            />
          </div>

          {/* Line height */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Altura da linha
              </Label>
              <span className="text-sm tabular-nums">{settings.lineHeight.toFixed(1)}</span>
            </div>
            <Slider
              min={1.2}
              max={2.4}
              step={0.1}
              value={[settings.lineHeight]}
              onValueChange={(v) => setSettings({ lineHeight: v[0] })}
            />
          </div>

          {/* Letter spacing */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Espaçamento de letras
              </Label>
              <span className="text-sm tabular-nums">{settings.letterSpacing.toFixed(1)}px</span>
            </div>
            <Slider
              min={-1}
              max={4}
              step={0.5}
              value={[settings.letterSpacing]}
              onValueChange={(v) => setSettings({ letterSpacing: v[0] })}
            />
          </div>

          {/* Margin width */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Largura das margens
              </Label>
              <span className="text-sm tabular-nums">{settings.marginWidth}%</span>
            </div>
            <Slider
              min={0}
              max={35}
              step={1}
              value={[settings.marginWidth]}
              onValueChange={(v) => setSettings({ marginWidth: v[0] })}
            />
          </div>

          {/* Toggles */}
          <div className="space-y-3 pt-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlignJustify className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm">Texto justificado</Label>
              </div>
              <Switch
                checked={settings.justify}
                onCheckedChange={(v) => setSettings({ justify: v })}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Pilcrow className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm">Hifenizar palavras</Label>
              </div>
              <Switch
                checked={settings.hyphenate}
                onCheckedChange={(v) => setSettings({ hyphenate: v })}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex !justify-between sm:!justify-between">
          <Button variant="ghost" size="sm" className="gap-2" onClick={resetSettings}>
            <RotateCcw className="h-3.5 w-3.5" />
            Restaurar padrões
          </Button>
          <Button size="sm" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
